import type { Edge, Node } from '@xyflow/react'
import {
  EMPTY_EXPOSURE,
  getExposure,
  type SubnetExposure,
} from '@/features/backtest/utils/subnet-exposure'
import { resolveConnectionLabel } from '@/features/backtest/components/nodes/node-utils'
import { buildEdgeAdjacency } from '@/features/backtest/utils/connection-utils'
import { NODE_WIDTHS } from '@/features/backtest/schemas/node-face'
import { nodeName } from '@/features/backtest/utils/node-display-name'

/** Subnet I/O pill node types. A subnet's input/output contract is defined by
 *  these pass-through pills — NOT by arbitrary internal nodes' schema ports.
 *  `subnetInput` exposes an external `in` target handle (where an outside wire
 *  lands); `subnetOutput` exposes an external `out` source handle. So a
 *  disconnected/pasted subnet's open I/O is derived from these pills. */
const SUBNET_INPUT_TYPE = 'subnetInput'
const SUBNET_OUTPUT_TYPE = 'subnetOutput'
/** External attach handle id on each pill (see use-node-operations grouping). */
const PILL_IN_HANDLE = 'in'
const PILL_OUT_HANDLE = 'out'

/** Handle id for an OPEN (unconnected) subnet input port — keyed by the internal
 *  target so a wire dropped on it can materialize an edge to that exact handle.
 *  Distinct prefix from `proxy-in:` (which is keyed by the external source). */
export const OPEN_INPUT_PREFIX = 'open-in:'
export const openInputHandleId = (childId: string, handle: string): string =>
  `${OPEN_INPUT_PREFIX}${childId}:${handle}`
export function parseOpenInputHandle(id: string): { nodeId: string; handleId: string } | null {
  if (!id.startsWith(OPEN_INPUT_PREFIX)) return null
  const rest = id.slice(OPEN_INPUT_PREFIX.length)
  const sep = rest.indexOf(':')
  if (sep < 0) return null
  return { nodeId: rest.slice(0, sep), handleId: rest.slice(sep + 1) }
}

/**
 * Nuke-style group collapse as a pure projection.
 *
 * The canonical graph (nodes, edges, parentId links) is never rewired by
 * collapsing. `applyCollapseState` derives what React Flow should render:
 * descendants of collapsed groups get `hidden`, edges crossing a collapsed
 * boundary get `hidden` plus a synthetic proxy edge rerouted to the group's
 * proxy handles. Expanding is just re-running the projection with the flag
 * off — there is no restore bookkeeping. Serialization must strip proxy
 * edges (`isProxyEdge`) so the backend always receives the full graph.
 */

// The wide node width (the And/Or gate's), so a closed subnet card sits in the
// graph at the same size as the gate it typically feeds, instead of reading as
// a slightly-off outlier. A plain node is the standard 280px.
export const COLLAPSED_GROUP_WIDTH = NODE_WIDTHS.wide
export const COLLAPSED_SUBNET_HEADER = 40
export const COLLAPSED_GROUP_MIN_HEIGHT = 72
// The collapsed card draws with the face rules every node uses (Chris,
// 2026-10-08: "The padding rules on the other nodes don't match the subnet").
// Each value is pinned to backtest-reactflow.css by collapsed-card-css.unit.test.ts.
/** A section heading: one line of `.node-section__heading` (1rem). */
export const COLLAPSED_SECTION_LABEL_HEIGHT = 16
/** An input or condition row: --node-row-height (1.75rem), the field box. */
export const COLLAPSED_IO_ROW_HEIGHT = 28
/** Between a heading and its rows, and between rows: --node-row-gap. */
export const COLLAPSED_ROW_GAP = 4
/** Under the conditions' heading, as the Stock Selection's: --node-label-gap. */
export const COLLAPSED_LABEL_GAP = 6
/** The output row and its gap, as every outputs section draws them. */
export const COLLAPSED_OUTPUT_ROW_HEIGHT = 16
export const COLLAPSED_OUTPUT_ROW_GAP = 8
export const COLLAPSED_CONTENT_PADDING = 16
/**
 * Divider between sections, as `node-divider-b` draws it: padding and margin
 * of --node-divider-gap (0.75rem each) plus a 1px line. Pinned to the CSS by
 * collapsed-card-css.unit.test.ts (at 17 the Output row hung below the card).
 */
export const COLLAPSED_SECTION_GAP = 25

export interface ProxyHandleSpec {
  id: string
  side: 'in' | 'out'
  /** Inputs: the EXTERNAL source node. Output: the internal source (or the group). */
  refNodeId: string
  refHandleId: string | null
  label: string
}

export interface GroupBoundary {
  inputs: ProxyHandleSpec[]
  outputs: ProxyHandleSpec[]
}

export const proxyHandleId = (
  side: 'in' | 'out',
  internalNodeId: string,
  internalHandleId: string | null | undefined,
): string => `proxy-${side}:${internalNodeId}:${internalHandleId ?? ''}`

export const isProxyEdge = (edge: Edge): boolean => edge.data?.proxy === true

export const isCollapsedSubnet = (node: Node | undefined): boolean =>
  !!node && node.type === 'subnet' && node.data?.collapsed === true

const buildNodeMap = (nodes: Node[]): Map<string, Node> => new Map(nodes.map((n) => [n.id, n]))

export function getSubnetDescendants(groupId: string, nodes: Node[]): Set<string> {
  const childrenByParent = new Map<string, string[]>()
  for (const n of nodes) {
    if (!n.parentId) continue
    const siblings = childrenByParent.get(n.parentId)
    if (siblings) siblings.push(n.id)
    else childrenByParent.set(n.parentId, [n.id])
  }
  const descendants = new Set<string>()
  const queue = [...(childrenByParent.get(groupId) ?? [])]
  while (queue.length > 0) {
    const id = queue.pop()!
    if (descendants.has(id)) continue
    descendants.add(id)
    queue.push(...(childrenByParent.get(id) ?? []))
  }
  return descendants
}

/**
 * The TOPMOST collapsed group strictly above `nodeId`, or null. Topmost (not
 * nearest) is what makes nesting work: with inner and outer both collapsed,
 * everything projects onto the outer group.
 */
function topmostCollapsedAncestor(nodeId: string, byId: Map<string, Node>): string | null {
  let topmost: string | null = null
  let current = byId.get(nodeId)
  while (current?.parentId) {
    const parent = byId.get(current.parentId)
    if (!parent) break
    if (isCollapsedSubnet(parent)) topmost = parent.id
    current = parent
  }
  return topmost
}

const effectiveEndpoint = (nodeId: string, byId: Map<string, Node>): string =>
  topmostCollapsedAncestor(nodeId, byId) ?? nodeId

const handleLabel = (node: Node | undefined, handleId: string | null | undefined): string => {
  const base = nodeDisplayLabel(node)
  return handleId ? `${base} · ${handleId}` : base
}

/** As a person names it (utils/node-display-name); `node` when it is gone. */
const nodeDisplayLabel = (node: Node | undefined): string => (node ? nodeName(node) : 'node')

/** A collapsed group exposes exactly ONE output handle (Nuke semantics). */
export const groupOutputHandleId = (groupId: string): string => proxyHandleId('out', groupId, 'out')

/**
 * Resolve where a canonical edge renders while collapse is applied: effective
 * endpoints plus the proxy handles. Inputs are keyed by the EXTERNAL source
 * (so five edges from one Price Data become one row + one wire); the source
 * side always maps to the group's single output handle.
 */
function projectEdge(edge: Edge, byId: Map<string, Node>) {
  const effSource = effectiveEndpoint(edge.source, byId)
  const effTarget = effectiveEndpoint(edge.target, byId)
  const crossesSource = effSource !== edge.source
  const crossesTarget = effTarget !== edge.target
  const sourceHandle = crossesSource ? groupOutputHandleId(effSource) : (edge.sourceHandle ?? null)
  const sourceRefId = crossesSource ? effSource : edge.source
  const targetHandle = crossesTarget
    ? proxyHandleId('in', sourceRefId, sourceHandle)
    : (edge.targetHandle ?? null)
  return {
    effSource,
    effTarget,
    crossesSource,
    crossesTarget,
    sourceHandle,
    targetHandle,
    sourceRefId,
  }
}

/**
 * Canonical (hidden) edges that project onto the given proxy edge. Used by
 * reconnect-through: dragging a proxy's external end rewires all of these.
 */
export function getUnderlyingEdgeIds(proxy: Edge, nodes: Node[], edges: Edge[]): string[] {
  const byId = buildNodeMap(nodes)
  return edges
    .filter((e) => !isProxyEdge(e))
    .filter((e) => {
      const p = projectEdge(e, byId)
      return (
        (p.crossesSource || p.crossesTarget) &&
        p.effSource === proxy.source &&
        p.effTarget === proxy.target &&
        p.sourceHandle === (proxy.sourceHandle ?? null) &&
        p.targetHandle === (proxy.targetHandle ?? null)
      )
    })
    .map((e) => e.id)
}

/** Canonical edges projecting onto a specific input row of a collapsed group. */
export function getUnderlyingInputEdgeIds(
  groupId: string,
  inputHandleId: string,
  nodes: Node[],
  edges: Edge[],
): string[] {
  const byId = buildNodeMap(nodes)
  return edges
    .filter((e) => !isProxyEdge(e))
    .filter((e) => {
      const p = projectEdge(e, byId)
      return p.effTarget === groupId && p.targetHandle === inputHandleId
    })
    .map((e) => e.id)
}

/**
 * The single internal (node, handle) behind a group's output handle, or null
 * when the group has no/ambiguous outputs. Used to materialize a NEW canonical
 * edge when the user drags a wire from a collapsed group's output.
 */
export function resolveGroupOutputSource(
  groupId: string,
  nodes: Node[],
  edges: Edge[],
): { nodeId: string; handleId: string | null } | null {
  const descendants = getSubnetDescendants(groupId, nodes)
  const sources = new Map<string, { nodeId: string; handleId: string | null }>()
  for (const edge of edges) {
    if (isProxyEdge(edge)) continue
    if (descendants.has(edge.source) && !descendants.has(edge.target) && edge.target !== groupId) {
      sources.set(`${edge.source}:${edge.sourceHandle ?? ''}`, {
        nodeId: edge.source,
        handleId: edge.sourceHandle ?? null,
      })
    }
  }
  if (sources.size === 1) return [...sources.values()][0]
  if (sources.size > 1) return null // ambiguous — guard should have blocked this

  // No edge leaves the subnet (e.g. just pasted, disconnected): fall back to the
  // subnetOutput pill — the node that DEFINES the subnet's single output — so it
  // can still be wired out while collapsed.
  const byId = buildNodeMap(nodes)
  for (const id of descendants) {
    if (byId.get(id)?.type === SUBNET_OUTPUT_TYPE) {
      return { nodeId: id, handleId: PILL_OUT_HANDLE }
    }
  }
  return null
}

/**
 * Distinct internal (source node, handle) pairs feeding outside the group.
 * Collapse is only allowed when this is <= 1 — a collapsed group has a single
 * output. Works on the expanded group (ignores collapsed flags) so the UI can
 * guard before collapsing.
 */
export function countSubnetOutputs(groupId: string, nodes: Node[], edges: Edge[]): number {
  const descendants = getSubnetDescendants(groupId, nodes)
  const sources = new Set<string>()
  for (const edge of edges) {
    if (isProxyEdge(edge)) continue
    if (descendants.has(edge.source) && !descendants.has(edge.target) && edge.target !== groupId) {
      sources.add(`${edge.source}:${edge.sourceHandle ?? ''}`)
    }
  }
  return sources.size
}

/**
 * Proxy handles a collapsed group must expose: one input per distinct
 * EXTERNAL source feeding the group (grouped — not one per internal use),
 * and at most one output.
 */
export function computeBoundary(groupId: string, nodes: Node[], edges: Edge[]): GroupBoundary {
  const byId = buildNodeMap(nodes)
  const adjacency = buildEdgeAdjacency(nodes, edges)
  const inputs = new Map<string, ProxyHandleSpec>()
  const outputSources = new Map<string, { nodeId: string; handleId: string | null }>()

  for (const edge of edges) {
    if (isProxyEdge(edge)) continue
    const p = projectEdge(edge, byId)
    if (p.effSource === p.effTarget) continue

    if (p.effTarget === groupId && p.targetHandle) {
      if (!inputs.has(p.targetHandle)) {
        // Label the row with the COMPUTED signal feeding the internal consumer
        // (e.g. `Close-1m`, `EMA(72)`, `A > B`) — the same pill text the rest of
        // the canvas shows — rather than the bare source node name. The canonical
        // edge's real target (edge.target / edge.targetHandle) is the in-subnet
        // node receiving the wire, so resolving its input reproduces that pill.
        const signal = resolveConnectionLabel(
          edge.target,
          edge.targetHandle ?? '',
          nodes,
          edges,
          undefined,
          0,
          adjacency,
        )
        const label =
          signal && signal !== 'No Input' && signal !== 'Connected'
            ? signal
            : nodeDisplayLabel(byId.get(p.sourceRefId))
        inputs.set(p.targetHandle, {
          id: p.targetHandle,
          side: 'in',
          refNodeId: p.sourceRefId,
          refHandleId: edge.sourceHandle ?? null,
          label,
        })
      }
    }
    if (p.effSource === groupId) {
      outputSources.set(`${edge.source}:${edge.sourceHandle ?? ''}`, {
        nodeId: edge.source,
        handleId: edge.sourceHandle ?? null,
      })
    }
  }

  // Same source node contributing via different handles → disambiguate labels
  const inputSpecs = [...inputs.values()]
  const labelCounts = new Map<string, number>()
  for (const s of inputSpecs) labelCounts.set(s.label, (labelCounts.get(s.label) ?? 0) + 1)
  const finalInputs = inputSpecs.map((s) =>
    (labelCounts.get(s.label) ?? 0) > 1 && s.refHandleId
      ? { ...s, label: `${s.label} · ${s.refHandleId}` }
      : s,
  )

  // OPEN ports: a subnet's I/O contract is its subnetInput/subnetOutput pills.
  // Surface each input pill whose external `in` handle nothing feeds as an OPEN,
  // wireable row, so a disconnected/pasted subnet still shows — and lets you wire
  // — its inputs. Additive: a fully-wired pill is already surfaced by the
  // edge-derived path above (its `in` is in fedHandles), so connected subnets are
  // unchanged.
  const descendants = getSubnetDescendants(groupId, nodes)
  const fedHandles = new Set<string>()
  for (const e of edges) {
    if (isProxyEdge(e)) continue
    if (descendants.has(e.target)) fedHandles.add(`${e.target}:${e.targetHandle ?? ''}`)
  }
  const openInputs: ProxyHandleSpec[] = []
  for (const id of descendants) {
    const child = byId.get(id)
    if (child?.type !== SUBNET_INPUT_TYPE) continue
    if (fedHandles.has(`${child.id}:${PILL_IN_HANDLE}`)) continue
    openInputs.push({
      id: openInputHandleId(child.id, PILL_IN_HANDLE),
      side: 'in',
      refNodeId: child.id,
      refHandleId: PILL_IN_HANDLE,
      label: nodeDisplayLabel(child),
    })
  }

  const outputs: ProxyHandleSpec[] = []
  if (outputSources.size > 0) {
    // > 1 distinct source should be blocked by countSubnetOutputs before
    // collapsing; render a generic merged handle as graceful degradation.
    const single = outputSources.size === 1 ? [...outputSources.values()][0] : null
    outputs.push({
      id: groupOutputHandleId(groupId),
      side: 'out',
      refNodeId: single?.nodeId ?? groupId,
      refHandleId: single?.handleId ?? null,
      label: single ? handleLabel(byId.get(single.nodeId), single.handleId) : 'Output',
    })
  } else {
    // No external consumer (e.g. just pasted, disconnected): derive the output
    // from the subnetOutput pill — the node that DEFINES the subnet's single
    // output — so a disconnected subnet still exposes (and can wire) its output.
    for (const id of descendants) {
      const child = byId.get(id)
      if (child?.type !== SUBNET_OUTPUT_TYPE) continue
      outputs.push({
        id: groupOutputHandleId(groupId),
        side: 'out',
        refNodeId: child.id,
        refHandleId: PILL_OUT_HANDLE,
        label: nodeDisplayLabel(child),
      })
      break
    }
  }

  return { inputs: [...finalInputs, ...openInputs], outputs }
}

/** Single-line value row: name (+ inline original name when renamed) + control. */
export const EXPOSED_PARAM_COMPACT_ROW_HEIGHT = 26
export const EXPOSURE_HEADING_HEIGHT = 22
export const EXPOSURE_SEPARATOR_HEIGHT = 9
/** Discrete node-label row above each run's fields (every run, even one row). */
export const EXPOSURE_RUN_LABEL_HEIGHT = 18
/** "Parameters" section label + spacing above the items */
export const EXPOSURE_SECTION_PADDING = 26
/** Breathing room below an EXPANDED section's heading (minimized sit close). */
export const EXPOSURE_GROUP_GAP = 6

/**
 * Heading ids whose section is minimized on the card (heading row only).
 * Persisted on the subnet node's `data.exposureCollapsed` so the height model
 * and the rendered card agree. (Pre-section-model data stored member-node ids
 * here; those simply never match a heading id, so old cards load expanded.)
 */
export function getExposureCollapsed(data: unknown): Set<string> {
  const raw = (data as { exposureCollapsed?: unknown } | undefined)?.exposureCollapsed
  return new Set(Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [])
}

/**
 * Extra card height for the exposed-parameters section (0 when no param is
 * exposed). Items count IN LIST ORDER — the arrangement from the exposure
 * layout panel is the card's structure: a `heading` is a minimizable section
 * header, and every param/separator after it (until the next heading) belongs
 * to that section and hides while it's minimized. Items before the first
 * heading are always visible. EVERY run of consecutive same-node params
 * (even a single row) renders as a block: one discrete node-label row
 * (EXPOSURE_RUN_LABEL_HEIGHT) above its compact field rows — headings and
 * separators break runs, exactly like the card's splitRuns.
 */
export function collapsedExposureHeight(
  exposure: SubnetExposure,
  minimizedHeadingIds?: ReadonlySet<string>,
): number {
  if (!exposure.items.some((i) => i.kind === 'param')) return 0
  let h = EXPOSURE_SECTION_PADDING
  let sectionMinimized = false
  let prevNodeId: string | null = null
  let runLength = 0
  for (const item of exposure.items) {
    if (item.kind === 'heading') {
      h += EXPOSURE_HEADING_HEIGHT
      sectionMinimized = minimizedHeadingIds?.has(item.id) ?? false
      // Expanded sections get breathing room below; minimized sit close.
      if (!sectionMinimized) h += EXPOSURE_GROUP_GAP
      prevNodeId = null
      runLength = 0
      continue
    }
    if (sectionMinimized) continue
    if (item.kind === 'separator') {
      h += EXPOSURE_SEPARATOR_HEIGHT
      prevNodeId = null
      runLength = 0
      continue
    }
    runLength = item.nodeId === prevNodeId ? runLength + 1 : 1
    prevNodeId = item.nodeId
    // A new run opens with its node-label row.
    if (runLength === 1) h += EXPOSURE_RUN_LABEL_HEIGHT
    h += EXPOSED_PARAM_COMPACT_ROW_HEIGHT
  }
  return h
}

export function collapsedGroupHeight(
  boundary: GroupBoundary,
  exposure: SubnetExposure = EMPTY_EXPOSURE,
  collapsedNodeIds?: ReadonlySet<string>,
  /**
   * Lines of the Form's conditions the card lists (rows plus a "+ N more"):
   * a section of their own under its label (subnet-card-heights.ts). 0 when
   * the Form cannot read the subnet.
   */
  conditionLines = 0,
): number {
  // n rows, a row gap between each.
  const rows = (n: number) => n * COLLAPSED_IO_ROW_HEIGHT + Math.max(0, n - 1) * COLLAPSED_ROW_GAP
  const inputsHeight =
    boundary.inputs.length > 0
      ? COLLAPSED_SECTION_LABEL_HEIGHT + COLLAPSED_ROW_GAP + rows(boundary.inputs.length)
      : 0
  const conditionsHeight =
    conditionLines > 0
      ? COLLAPSED_SECTION_LABEL_HEIGHT + COLLAPSED_LABEL_GAP + rows(conditionLines)
      : 0
  const outputHeight =
    boundary.outputs.length > 0
      ? COLLAPSED_SECTION_LABEL_HEIGHT + COLLAPSED_OUTPUT_ROW_GAP + COLLAPSED_OUTPUT_ROW_HEIGHT
      : 0
  const exposureHeight = collapsedExposureHeight(exposure, collapsedNodeIds)
  const sections = [inputsHeight, conditionsHeight, exposureHeight, outputHeight].filter(
    (h) => h > 0,
  ).length
  const dividers = Math.max(0, sections - 1) * COLLAPSED_SECTION_GAP
  return Math.max(
    COLLAPSED_GROUP_MIN_HEIGHT,
    COLLAPSED_SUBNET_HEADER +
      COLLAPSED_CONTENT_PADDING +
      inputsHeight +
      conditionsHeight +
      exposureHeight +
      outputHeight +
      dividers,
  )
}

/**
 * Project collapse state onto the graph. Idempotent: stale proxy edges are
 * dropped and rebuilt from the canonical edges every time, so it can be
 * re-applied after any toggle, load, or structural change.
 */
export function applyCollapseState(nodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } {
  const byId = buildNodeMap(nodes)
  const canonical = edges.filter((e) => !isProxyEdge(e))

  const nextNodes = nodes.map((node) => {
    const hidden = topmostCollapsedAncestor(node.id, byId) !== null
    const hiddenChanged = (node.hidden ?? false) !== hidden

    // Size only changes for group nodes: a collapsed group shrinks to the fixed
    // card; an expanded one restores its stored expandedSize.
    let targetWidth: number | undefined
    let targetHeight: number | undefined
    if (node.type === 'subnet') {
      if (isCollapsedSubnet(node) && !hidden) {
        const boundary = computeBoundary(node.id, nodes, canonical)
        targetWidth = COLLAPSED_GROUP_WIDTH
        targetHeight = collapsedGroupHeight(
          boundary,
          getExposure(node.data),
          getExposureCollapsed(node.data),
        )
      } else if (!isCollapsedSubnet(node)) {
        const expandedSize = node.data?.expandedSize as
          | { width: number; height: number }
          | undefined
        if (expandedSize) {
          targetWidth = expandedSize.width
          targetHeight = expandedSize.height
        }
      }
    }
    const sizeChanged =
      targetWidth !== undefined && (node.width !== targetWidth || node.height !== targetHeight)

    // Identity-preserving: when nothing about this node changes, return the SAME
    // object reference. React Flow and `enhancedNodes` short-circuit on
    // reference equality, so collapsing/expanding a subnet only re-renders the
    // subnet and its descendants — not the whole graph.
    if (!hiddenChanged && !sizeChanged) return node

    let next: Node = { ...node, hidden }
    if (sizeChanged) {
      // Size is written to BOTH style and the top-level width/height props:
      // React Flow renders props over style, and expandParent stamps props on
      // grown groups — style alone would leave the card at full size.
      next = {
        ...next,
        width: targetWidth,
        height: targetHeight,
        style: { ...next.style, width: targetWidth, height: targetHeight },
      }
    }
    return next
  })

  // Wire shapes (control points) given to proxies survive re-projection:
  // prefer the live proxy's points, then shapes persisted on the owning group.
  const priorProxies = new Map(edges.filter(isProxyEdge).map((e) => [e.id, e]))

  // Deduped by endpoint tuple: several canonical edges from one external
  // source project onto a single visual wire.
  const proxyByKey = new Map<string, Edge>()
  const nextEdges = canonical.map((edge) => {
    const p = projectEdge(edge, byId)

    if (!p.crossesSource && !p.crossesTarget) return { ...edge, hidden: false }

    if (p.effSource !== p.effTarget) {
      const key = `${p.effSource}|${p.sourceHandle}|${p.effTarget}|${p.targetHandle}`
      if (!proxyByKey.has(key)) {
        const proxyId = `proxy:${key}`
        const ownerId = p.crossesTarget ? p.effTarget : p.effSource
        const ownerData = byId.get(ownerId)?.data as
          | { proxyEdgePoints?: Record<string, unknown> }
          | undefined
        const points =
          (priorProxies.get(proxyId)?.data as { points?: unknown } | undefined)?.points ??
          ownerData?.proxyEdgePoints?.[proxyId]
        proxyByKey.set(key, {
          id: proxyId,
          source: p.effSource,
          target: p.effTarget,
          sourceHandle: p.sourceHandle,
          targetHandle: p.targetHandle,
          type: edge.type,
          data: { proxy: true, originalEdgeId: edge.id, ...(points ? { points } : {}) },
        })
      }
    }
    return { ...edge, hidden: true }
  })

  return { nodes: nextNodes, edges: [...nextEdges, ...proxyByKey.values()] }
}

/**
 * Toggle a subnet's collapsed flag and re-derive the projection + framing,
 * centering the card within (or restoring from) the expanded footprint. This is
 * the canonical collapse/expand geometry — used by the subnet node's own toggle
 * and by the editor's "open subnet" takeover (which expands on enter and
 * re-collapses on exit). Pure: callers own snapshotting + the collapsibility
 * guard. No-op (returns the inputs unchanged) if already in the target state or
 * the subnet is missing.
 */
export function setSubnetCollapsed(
  subnetId: string,
  collapsed: boolean,
  nodes: Node[],
  edges: Edge[],
): { nodes: Node[]; edges: Edge[] } {
  const me = nodes.find((n) => n.id === subnetId)
  if (!me) return { nodes, edges }
  if ((me.data?.collapsed === true) === collapsed) return { nodes, edges }

  // The size the frame was SET to (a fit in the same pass writes it) wins over React
  // Flow's measured size, which lags a render behind and would record the pre-fit frame.
  const measuredSize = {
    width: Number(me.width ?? (me.style?.width as number) ?? me.measured?.width ?? 600),
    height: Number(me.height ?? (me.style?.height as number) ?? me.measured?.height ?? 400),
  }

  const updatedNodes = nodes.map((n) =>
    n.id === subnetId
      ? {
          ...n,
          data: {
            ...n.data,
            collapsed,
            ...(collapsed ? { expandedSize: measuredSize } : {}),
          },
        }
      : n,
  )

  const projected = applyCollapseState(updatedNodes, edges)

  // Keep the card centered on the expanded footprint across the toggle: collapse
  // shifts the origin by half the size delta; expand reverses it.
  let finalNodes = projected.nodes
  if (collapsed) {
    const card = projected.nodes.find((n) => n.id === subnetId)
    const cw = Number(card?.width ?? (card?.style?.width as number) ?? 0)
    const ch = Number(card?.height ?? (card?.style?.height as number) ?? 0)
    const dx = (measuredSize.width - cw) / 2
    const dy = (measuredSize.height - ch) / 2
    finalNodes = projected.nodes.map((n) =>
      n.id === subnetId ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } : n,
    )
  } else {
    const expandedSize = me.data?.expandedSize as { width: number; height: number } | undefined
    const cw = Number(me.measured?.width ?? (me.style?.width as number) ?? 0)
    const ch = Number(me.measured?.height ?? (me.style?.height as number) ?? 0)
    if (expandedSize && cw && ch) {
      const dx = (expandedSize.width - cw) / 2
      const dy = (expandedSize.height - ch) / 2
      finalNodes = projected.nodes.map((n) =>
        n.id === subnetId ? { ...n, position: { x: n.position.x - dx, y: n.position.y - dy } } : n,
      )
    }
  }

  return { nodes: finalNodes, edges: projected.edges }
}
